// A width the author sets by dragging a divider, remembered per browser.
//
// The returned `handle` props go on the divider element: drag with a mouse,
// pen or finger; ← / → move it 16 px (Shift: 64 px); double-click puts it back
// to the default. The width is clamped to [min, max()] on every change and
// whenever the window resizes, so a width saved on a big screen never pushes
// the chart off a smaller one. Storage is a convenience: an unreadable store
// just means the default.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent as ReactPointerEvent } from 'react'

const PREFIX = 'twasta.visualDesigner.'

function read(key: string): number | null {
  try {
    const n = Number(window.localStorage.getItem(key))
    return Number.isFinite(n) && n > 0 ? n : null
  } catch { return null }
}
function write(key: string, v: number) {
  try { window.localStorage.setItem(key, String(Math.round(v))) } catch { /* private window */ }
}

export interface DragWidthOptions {
  /** The divider sits on the element's LEFT edge (a panel on the right):
   *  dragging left widens it. */
  fromRight?: boolean
  /** Storage prefix (default: the visual designer's). */
  prefix?: string
}

export function useDragWidth(
  storageKey: string, fallback: number, min: number, max: () => number, label: string,
  opts: DragWidthOptions = {},
) {
  const key = (opts.prefix ?? PREFIX) + storageKey
  const sign = opts.fromRight ? -1 : 1
  const clamp = useCallback((v: number) => Math.round(Math.max(min, Math.min(v, Math.max(min, max())))), [min, max])
  const [width, setWidthState] = useState<number>(() => read(key) ?? fallback)
  const [dragging, setDragging] = useState(false)
  const start = useRef<{ x: number; w: number } | null>(null)

  const setWidth = useCallback((v: number, persist = true) => {
    const c = clamp(v)
    setWidthState(c)
    if (persist) write(key, c)
  }, [clamp, key])

  // Keep a remembered width inside the space this screen has.
  useEffect(() => {
    const fit = () => setWidthState((w) => clamp(w))
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [clamp])

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    start.current = { x: e.clientX, w: width }
    setDragging(true)
    const el = e.currentTarget
    try { el.setPointerCapture(e.pointerId) } catch { /* older browsers */ }
    const move = (ev: PointerEvent) => {
      if (!start.current) return
      setWidthState(clamp(start.current.w + sign * (ev.clientX - start.current.x)))
    }
    const up = (ev: PointerEvent) => {
      el.removeEventListener('pointermove', move)
      el.removeEventListener('pointerup', up)
      el.removeEventListener('pointercancel', up)
      if (start.current) write(key, clamp(start.current.w + sign * (ev.clientX - start.current.x)))
      start.current = null
      setDragging(false)
    }
    el.addEventListener('pointermove', move)
    el.addEventListener('pointerup', up)
    el.addEventListener('pointercancel', up)
  }, [width, clamp, key, sign])

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
    const step = e.shiftKey ? 64 : 16
    if (e.key === 'ArrowLeft') { e.preventDefault(); setWidth(width - sign * step) }
    else if (e.key === 'ArrowRight') { e.preventDefault(); setWidth(width + sign * step) }
    else if (e.key === 'Home') { e.preventDefault(); setWidth(min) }
    else if (e.key === 'End') { e.preventDefault(); setWidth(max()) }
  }, [width, setWidth, min, max, sign])

  const handle = {
    role: 'separator' as const,
    tabIndex: 0,
    'aria-orientation': 'vertical' as const,
    'aria-label': label,
    'aria-valuenow': width,
    'aria-valuemin': min,
    title: `${label} — drag to resize, double-click to reset`,
    onPointerDown,
    onKeyDown,
    onDoubleClick: () => setWidth(fallback),
  }
  return { width, setWidth, dragging, handle }
}
